import { useSession } from "@/lib/auth-client";
import { stopImpersonating } from "@/utils/admin-actions";
import { Button } from "@papier/ui/components/ui/button";
import { useQueryClient } from "@tanstack/react-query";
import { ShieldWarning as ShieldAlert } from "@papier/ui/components/icons";
import { useState } from "react";
import { toast } from "sonner";

/** Visible even when the impersonated account has no organization or cannot enter the app. */
export function ImpersonationBanner() {
  const { data: session } = useSession();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  if (!session?.session.impersonatedBy) return null;

  const stop = async () => {
    setPending(true);
    try {
      await stopImpersonating();
      await queryClient.cancelQueries();
      queryClient.clear();
      // Identity changes must also discard Router and Better Auth UI caches.
      window.location.assign("/admin/users");
    } catch (error) {
      toast.error("Could not stop impersonating", {
        description: error instanceof Error ? error.message : "Try again.",
      });
      setPending(false);
    }
  };

  return (
    <aside
      aria-label="Impersonation session"
      className="sticky top-0 z-50 flex flex-wrap items-center justify-between gap-3 bg-warning/15 px-4 py-3 text-sm"
    >
      <div className="flex min-w-0 items-center gap-2">
        <ShieldAlert className="size-5 shrink-0 text-warning" />
        <p className="min-w-0 break-words">
          Acting as <strong>{session.user.name}</strong>{" "}
          <span className="break-all text-muted-foreground">({session.user.email})</span>. Actions
          use this user’s permissions.
        </p>
      </div>
      <Button
        className="shrink-0"
        size="lg"
        variant="outline"
        disabled={pending}
        isLoading={pending}
        onClick={stop}
      >
        Stop impersonating
      </Button>
    </aside>
  );
}
